import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import client from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import MetricStat from '../components/MetricStat';
import AnalysisShortcutBar from '../components/AnalysisShortcutBar';
import FacilityMapModal from '../components/FacilityMapModal';
import ViewModeToggle from '../components/ViewModeToggle';
import { useViewMode } from '../hooks/useViewMode';
import '../components/ExpandableList.css';
import { subscribeCacheRefreshed } from '../utils/cacheEvents';
import {
  COMPARISON_PRESETS,
  DEFAULT_COMPARISON_PRESET,
  computeComparisonRange,
  computeWeekComparisonRange,
} from '../utils/comparisonPeriods';
import { buildWeeklyHighlights } from '../utils/welcomeWeeklyHighlights';
import {
  getCachedCustomers, setCachedCustomers,
  getCachedMetrics,   setCachedMetrics,
  getCachedStats,     setCachedStats,
  metricsCacheKey,    statsIdsKey,
  isFresh,            invalidateWelcomeSessionCache,
} from '../utils/welcomeSessionCache';
import './Welcome.css';

// LocalStorage key for the user's preferred comparison preset on the
// Home dashboard. Persists across reloads / sessions; per-tab persistence
// would be confusing since this is a setting, not a transient navigation
// state.
const COMPARISON_PREF_KEY = 'welcome-comparison-preset';

// LocalStorage keys for the customer-card sort. The sort mode is the
// dropdown selection; the manual order is an array of customer IDs that
// only matters when sortMode === 'manual'. We keep them separate so
// flipping into/out of manual mode doesn't lose the manual ordering
// the user set up.
const SORT_MODE_KEY = 'welcome-sort-mode';
const MANUAL_ORDER_KEY = 'welcome-manual-order';
const SORT_MODES = [
  { id: 'alphabetical',   label: 'Alphabetical (A → Z)' },
  { id: 'manual',         label: 'Manual (drag to reorder)' },
  { id: 'referrals-desc', label: 'Most referrals → least' },
  { id: 'score-desc',     label: 'Best → worst (overall score)' },
];
const DEFAULT_SORT_MODE = 'alphabetical';

// Composite "overall score" used by the score-desc sort. Referrals are
// the headline product metric so they get the heaviest weight; the
// other counts are supporting signal. Trend (current vs previous) is
// folded in as a multiplier so a customer who's slipping ranks below
// one with the same raw volume but a healthy trend.
function computeCustomerScore(metrics) {
  if (!metrics) return 0;
  const c = metrics.current || {};
  const p = metrics.previous || {};
  const weighted =
    3 * (Number(c.referrals)   || 0) +
    2 * (Number(c.vitalRounds) || 0) +
    1 * (Number(c.logins)      || 0) +
    1 * (Number(c.messages)    || 0) +
    1 * (Number(c.studies)     || 0);
  // Trend multiplier: -10% to +10% on top of the raw weighted volume.
  // Uses referrals because it's the metric the user cares about most.
  const prevR = Number(p.referrals) || 0;
  const currR = Number(c.referrals) || 0;
  let trendMult = 1;
  if (prevR > 0) {
    const pct = (currR - prevR) / prevR;
    trendMult = 1 + Math.max(-0.1, Math.min(0.1, pct));
  }
  return weighted * trendMult;
}

function HealthDot({ color, size = 18, title }) {
  const bg = color === 'green' ? '#10b981'
    : color === 'yellow' ? '#f59e0b'
    : color === 'red' ? '#ef4444'
    : '#cbd5e1';
  return (
    <span
      className="welcome-health-dot"
      title={title || color || 'unknown'}
      style={{ width: size, height: size, background: bg }}
    />
  );
}

function formatMoney(amount, frequency) {
  if (amount == null) return null;
  const n = Number(amount);
  if (Number.isNaN(n)) return null;
  const formatted = n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  return frequency ? `${formatted} / ${frequency.toLowerCase()}` : formatted;
}

function formatRenewal(startDate, termMonths) {
  if (!startDate || !termMonths) return null;
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return null;
  d.setMonth(d.getMonth() + Number(termMonths));
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/** Normalize API csRepDisplayNames (camelCase / PascalCase / occasional JSON string). */
function normalizeCsRepDisplayNames(customer) {
  const raw = customer?.csRepDisplayNames ?? customer?.CsRepDisplayNames;
  if (Array.isArray(raw)) return raw.map(String).map(s => s.trim()).filter(Boolean);
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return [];
    if (t.startsWith('[')) {
      try {
        const p = JSON.parse(t);
        return Array.isArray(p) ? p.map(String).map(s => s.trim()).filter(Boolean) : [];
      } catch {
        return [t];
      }
    }
    return [t];
  }
  return [];
}

function WelcomeLoadProgress({ steps }) {
  const doneCount = steps.filter(s => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const active = steps.find(s => !s.done);

  return (
    <div className="welcome-load-progress" role="status" aria-live="polite">
      <div className="welcome-load-progress-track" aria-hidden="true">
        <div className="welcome-load-progress-bar" style={{ width: `${pct}%` }} />
      </div>
      <p className="welcome-load-progress-label">
        Loading dashboard… {active ? active.label : 'Finishing up'}
        {' '}({doneCount}/{steps.length})
      </p>
      <div className="welcome-load-progress-steps">
        {steps.map(s => (
          <span key={s.id} className={`welcome-load-step ${s.done ? 'is-done' : s.active ? 'is-active' : ''}`}>
            {s.done ? '✓' : '○'} {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function WelcomeSkeletonCard() {
  return (
    <div className="welcome-card welcome-card-skeleton" aria-hidden="true">
      <div className="welcome-skeleton-line welcome-skeleton-title" />
      <div className="welcome-skeleton-line welcome-skeleton-sub" />
      <div className="welcome-skeleton-metrics">
        {[1, 2, 3, 4, 5].map(n => (
          <div key={n} className="welcome-skeleton-metric" />
        ))}
      </div>
    </div>
  );
}

function Welcome() {
  const { user, customers: myAssignments } = useAuth();
  // Initial state is hydrated from the module-level session cache so a
  // re-mount (e.g. navigating back from Data Trends) renders instantly with
  // whatever was last loaded. Effects below decide whether a background
  // refresh is needed based on each entry's TTL. See welcomeSessionCache.js
  // for the full rationale.
  const [customers, setCustomers] = useState(() => getCachedCustomers()?.data || []);
  const [customerStats, setCustomerStats] = useState(() => {
    // We don't yet know which customer-id key the cache was stored under
    // (that requires the customers list); just hydrate whatever's there.
    // The stats effect will reconcile if the keyed entry doesn't match.
    const e = getCachedStats(statsIdsKey(getCachedCustomers()?.data || []));
    return e?.data || {};
  });
  const [metricsByCustomer, setMetricsByCustomer] = useState(() => {
    try {
      const stored = localStorage.getItem(COMPARISON_PREF_KEY);
      const presetId = stored && COMPARISON_PRESETS.some(p => p.id === stored)
        ? stored
        : DEFAULT_COMPARISON_PRESET;
      const cached = getCachedMetrics(metricsCacheKey(computeComparisonRange(presetId)));
      return cached?.data || {};
    } catch {
      return {};
    }
  });
  const [customersLoading, setCustomersLoading] = useState(() => !getCachedCustomers()?.data);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  // Subtle "refreshing" hint for stale-while-revalidate background fetches.
  // Surfaced in the period-bar copy alongside the existing metricsLoading
  // text so the user knows fresh numbers are on the way without seeing the
  // cards flash empty. Counter-based so the indicator stays on until ALL
  // in-flight loaders finish, not just the first one (otherwise it would
  // flicker off when load() returns while loadStats() is still running).
  const [bgRefreshing, setBgRefreshing] = useState(false);
  const bgInflightRef = useRef(0);
  const startBg = useCallback(() => {
    bgInflightRef.current += 1;
    setBgRefreshing(true);
  }, []);
  const endBg = useCallback(() => {
    bgInflightRef.current = Math.max(0, bgInflightRef.current - 1);
    if (bgInflightRef.current === 0) setBgRefreshing(false);
  }, []);

  // Comparison preset controls which two windows the metric tile colors
  // compare. Persists in localStorage so the user's choice sticks across
  // sessions. Default = Last 3 full months vs prior 3 full months.
  const [comparisonPresetId, setComparisonPresetId] = useState(() => {
    try {
      const stored = localStorage.getItem(COMPARISON_PREF_KEY);
      if (stored && COMPARISON_PRESETS.some(p => p.id === stored)) return stored;
    } catch { /* localStorage may be unavailable in some environments */ }
    return DEFAULT_COMPARISON_PRESET;
  });
  useEffect(() => {
    try { localStorage.setItem(COMPARISON_PREF_KEY, comparisonPresetId); }
    catch { /* ignore */ }
  }, [comparisonPresetId]);

  // Concrete date strings for the API call + UI labels. Recomputed when
  // the preset changes (and once per mount). We don't recompute by-the-
  // minute — the page would have to be open across midnight on the first
  // of a month for it to matter, and the user can refresh.
  const comparisonRange = useMemo(
    () => computeComparisonRange(comparisonPresetId),
    [comparisonPresetId]
  );

  // Business-day week window — recomputes when the calendar day changes so
  // "data through yesterday" stays correct if the tab stays open overnight.
  const todayKey = new Date().toISOString().slice(0, 10);
  const weekRange = useMemo(
    () => computeWeekComparisonRange(new Date(`${todayKey}T12:00:00`)),
    [todayKey],
  );
  const [weeklyMetricsByCustomer, setWeeklyMetricsByCustomer] = useState(() => {
    const cached = getCachedMetrics(metricsCacheKey(computeWeekComparisonRange()));
    return cached?.data || {};
  });
  const [weeklyMetricsLoading, setWeeklyMetricsLoading] = useState(false);

  // Card sort mode. Default = alphabetical. Manual order is stored
  // separately so flipping in/out of manual doesn't destroy whatever
  // ordering the user set up.
  const [viewMode, setViewMode] = useViewMode('welcome');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [sortMode, setSortMode] = useState(() => {
    try {
      const stored = localStorage.getItem(SORT_MODE_KEY);
      if (stored && SORT_MODES.some(m => m.id === stored)) return stored;
    } catch { /* ignore */ }
    return DEFAULT_SORT_MODE;
  });
  useEffect(() => {
    try { localStorage.setItem(SORT_MODE_KEY, sortMode); }
    catch { /* ignore */ }
  }, [sortMode]);

  const toggleExpanded = useCallback((id) => {
    const numId = Number(id);
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(numId)) next.delete(numId);
      else next.add(numId);
      return next;
    });
  }, []);

  const [manualOrder, setManualOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(MANUAL_ORDER_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(Number).filter(Number.isFinite) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(MANUAL_ORDER_KEY, JSON.stringify(manualOrder)); }
    catch { /* ignore */ }
  }, [manualOrder]);

  // Tracks which card is currently being dragged so the drop targets
  // can render an insertion indicator. null when no drag is in flight.
  const [dragId, setDragId] = useState(null);

  // ---------------------------------------------------------------------
  // Loaders
  //
  // Each loader writes back to the session cache on success and accepts
  // a { background } flag. background=true skips the foreground spinner
  // (`loading` / `metricsLoading`) so the existing UI stays on screen
  // during a stale-while-revalidate refresh; the bgRefreshing pill in the
  // period bar is the user-facing signal that something is in flight.
  // ---------------------------------------------------------------------

  const load = useCallback(async ({ background = false } = {}) => {
    const cached = getCachedCustomers();
    if (!background) setCustomersLoading(true);
    else startBg();

    let quickPaintDone = false;
    try {
      // Fast first paint on cold load: customer names without the expensive
      // health computation. Cards appear while includeHealth=true finishes.
      if (!background && !cached?.data) {
        try {
          const quickRes = await client.get('/api/customers');
          const quick = quickRes.data?.customers || [];
          if (quick.length) {
            setCustomers(quick);
            setCustomersLoading(false);
            quickPaintDone = true;
          }
        } catch {
          /* full fetch below */
        }
      }

      const cRes = await client.get('/api/customers?includeHealth=true');
      const data = cRes.data?.customers || [];
      setCachedCustomers(data);
      setCustomers(data);
    } finally {
      if (!quickPaintDone && !background) setCustomersLoading(false);
      if (background) endBg();
    }
  }, [startBg, endBg]);

  // Metrics live on their own effect so the dropdown can refire just the
  // metrics call without re-fetching customers / filters.
  const loadMetrics = useCallback(async (range, { background = false } = {}) => {
    if (!background) setMetricsLoading(true);
    else startBg();
    try {
      const params = new URLSearchParams({
        currentStart:  range.currentStart,
        currentEnd:    range.currentEnd,
        previousStart: range.previousStart,
        previousEnd:   range.previousEnd,
      });
      const res = await client.get(`/api/customers/metrics?${params.toString()}`);
      const data = res.data?.metrics || {};
      setCachedMetrics(metricsCacheKey(range), data);
      setMetricsByCustomer(data);
    } catch {
      // On error, keep whatever's already on screen (cached) rather than
      // wiping the metric tiles to em-dashes — the previous values are
      // still strictly more useful than nothing.
      if (!getCachedMetrics(metricsCacheKey(range))) setMetricsByCustomer({});
    } finally {
      if (!background) setMetricsLoading(false);
      else endBg();
    }
  }, [startBg, endBg]);

  const loadStats = useCallback(async (keySource, { background = false } = {}) => {
    if (background) startBg();
    else setStatsLoading(true);
    try {
      const res = await client.get('/api/customers/facility-stats');
      const raw = res.data?.stats || {};
      setCachedStats(raw, statsIdsKey(keySource || []));
      setCustomerStats(raw);
    } finally {
      if (!background) setStatsLoading(false);
      else endBg();
    }
  }, [startBg, endBg]);

  const loadWeeklyMetrics = useCallback(async ({ background = false } = {}) => {
    const range = computeWeekComparisonRange();
    const key = metricsCacheKey(range);
    if (!range.hasBusinessDays) {
      setWeeklyMetricsByCustomer({});
      return;
    }
    if (!background) setWeeklyMetricsLoading(true);
    else startBg();
    try {
      const params = new URLSearchParams({
        currentStart:  range.currentStart,
        currentEnd:    range.currentEnd,
        previousStart: range.previousStart,
        previousEnd:   range.previousEnd,
      });
      const res = await client.get(`/api/customers/metrics?${params.toString()}`);
      const data = res.data?.metrics || {};
      setCachedMetrics(key, data);
      setWeeklyMetricsByCustomer(data);
    } catch {
      if (!getCachedMetrics(key)) setWeeklyMetricsByCustomer({});
    } finally {
      if (!background) setWeeklyMetricsLoading(false);
      else endBg();
    }
  }, [startBg, endBg]);

  // ---------------------------------------------------------------------
  // Effects (SWR)
  //
  // For every cache slot:
  //   - If fresh:        do nothing, the initial state is already correct.
  //   - If stale + has data: trigger a background refresh.
  //   - If empty:        trigger a foreground load.
  // ---------------------------------------------------------------------

  // Customers
  useEffect(() => {
    const cached = getCachedCustomers();
    if (isFresh(cached)) return;
    load({ background: !!cached?.data });
  }, [load]);

  // Metrics — keyed by the comparison window, so flipping presets back to a
  // previously-seen value renders instantly.
  useEffect(() => {
    const key = metricsCacheKey(comparisonRange);
    const cached = getCachedMetrics(key);
    if (cached?.data) setMetricsByCustomer(cached.data);
    if (isFresh(cached)) return;
    loadMetrics(comparisonRange, { background: !!cached?.data });
  }, [comparisonRange, loadMetrics]);

  // Weekly briefing metrics — always this week vs same days last week.
  useEffect(() => {
    const key = metricsCacheKey(weekRange);
    const cached = getCachedMetrics(key);
    if (cached?.data) setWeeklyMetricsByCustomer(cached.data);
    if (isFresh(cached)) return;
    loadWeeklyMetrics({ background: !!cached?.data });
  }, [weekRange, loadWeeklyMetrics]);

  // Resolved-facility stats — keyed by the customer-id set. Starts in
  // parallel with customers/metrics (does not wait for the customer list).
  useEffect(() => {
    const keySource = customers.length ? customers : (myAssignments || []);
    const idsKey = statsIdsKey(keySource);
    if (!idsKey) return;
    const cached = getCachedStats(idsKey);
    if (cached?.data) setCustomerStats(cached.data);
    if (isFresh(cached)) return;
    loadStats(keySource, { background: !!cached?.data });
  }, [customers, myAssignments, loadStats]);

  // Refetch when an admin runs a refresh on Data Sources (in this tab or any
  // other tab on the same origin) so the customer cards reflect the new data
  // without requiring a page reload. We blow away the session cache first so
  // the next mount of this page would also see fresh data.
  useEffect(() => subscribeCacheRefreshed(() => {
    invalidateWelcomeSessionCache();
    load();
    loadMetrics(comparisonRange);
    loadWeeklyMetrics();
    // loadStats reruns automatically when customers updates from `load`.
  }), [load, loadMetrics, loadWeeklyMetrics, comparisonRange]);

  // Home dashboard scope: only the customers this user is directly assigned to,
  // regardless of role. Admin/Manager users still see every customer on the
  // /admin/customers page; this filter is just for "your customers" tiles here.
  // Each assigned customer (master, sub-deal, or standalone) gets its own card.
  // The childrenByParent map stays full so a visible master can chip its
  // sub-deals — but the card render filters those chips down to sub-deals that
  // aren't already shown as their own card, to avoid duplication.
  const myCustomerIds = useMemo(
    () => new Set((myAssignments || []).map(c => Number(c.id))),
    [myAssignments]
  );

  const { roots, childrenByParent } = useMemo(() => {
    const map = new Map();
    for (const c of customers) {
      if (c.parentCustomerId) {
        const pid = Number(c.parentCustomerId);
        if (!map.has(pid)) map.set(pid, []);
        map.get(pid).push(c);
      }
    }
    return {
      roots: customers.filter(c => myCustomerIds.has(Number(c.id))),
      childrenByParent: map,
    };
  }, [customers, myCustomerIds]);

  // Apply the selected sort to roots. Score / referral sorts depend on
  // metricsByCustomer which loads asynchronously — that's fine; the
  // memo will recompute and re-render when metrics arrive.
  const sortedRoots = useMemo(() => {
    if (!roots.length) return roots;
    const arr = [...roots];

    if (sortMode === 'alphabetical') {
      arr.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
      return arr;
    }

    if (sortMode === 'referrals-desc') {
      arr.sort((a, b) => {
        const ar = Number(metricsByCustomer[String(a.id)]?.current?.referrals) || 0;
        const br = Number(metricsByCustomer[String(b.id)]?.current?.referrals) || 0;
        if (br !== ar) return br - ar;
        return (a.name || '').localeCompare(b.name || '');
      });
      return arr;
    }

    if (sortMode === 'score-desc') {
      arr.sort((a, b) => {
        const as = computeCustomerScore(metricsByCustomer[String(a.id)]);
        const bs = computeCustomerScore(metricsByCustomer[String(b.id)]);
        if (bs !== as) return bs - as;
        return (a.name || '').localeCompare(b.name || '');
      });
      return arr;
    }

    // 'manual': respect saved order, then append any unseen customers
    // (newly assigned, etc.) at the end in alphabetical order so they
    // don't just disappear.
    if (sortMode === 'manual') {
      const orderIndex = new Map();
      manualOrder.forEach((id, i) => orderIndex.set(Number(id), i));
      arr.sort((a, b) => {
        const ai = orderIndex.has(Number(a.id)) ? orderIndex.get(Number(a.id)) : Number.POSITIVE_INFINITY;
        const bi = orderIndex.has(Number(b.id)) ? orderIndex.get(Number(b.id)) : Number.POSITIVE_INFINITY;
        if (ai !== bi) return ai - bi;
        // Both unranked — fall back to alphabetical
        return (a.name || '').localeCompare(b.name || '');
      });
      return arr;
    }

    return arr;
  }, [roots, sortMode, metricsByCustomer, manualOrder]);

  // While the full customer list is loading, show assigned customer names
  // from auth so cards appear immediately instead of a blank page.
  const placeholderRoots = useMemo(() => {
    if (customers.length > 0 || !customersLoading) return [];
    return (myAssignments || []).map(a => ({
      id: a.id,
      name: a.name,
      health: null,
      parentCustomerId: a.parentCustomerId,
      _placeholder: true,
    }));
  }, [customers.length, customersLoading, myAssignments]);

  const displayRoots = sortedRoots.length > 0 ? sortedRoots : placeholderRoots;

  const loadSteps = useMemo(() => [
    { id: 'customers', label: 'Customer info', done: !customersLoading, active: customersLoading },
    { id: 'metrics', label: 'Activity metrics', done: !metricsLoading, active: metricsLoading },
    { id: 'stats', label: 'Facilities', done: !statsLoading, active: statsLoading },
  ], [customersLoading, metricsLoading, statsLoading]);

  const showLoadProgress = customersLoading || metricsLoading || statsLoading;

  // Drag handlers (manual mode only). Native HTML5 D&D — no extra
  // library needed. The card's onClick still fires for plain clicks
  // because the browser distinguishes click from drag.
  const handleDragStart = (id) => (e) => {
    setDragId(Number(id));
    // Required for Firefox to actually initiate the drag.
    try { e.dataTransfer.setData('text/plain', String(id)); } catch { /* ignore */ }
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e) => {
    if (sortMode !== 'manual' || dragId == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (targetId) => (e) => {
    if (sortMode !== 'manual' || dragId == null) return;
    e.preventDefault();
    const src = Number(dragId);
    const dst = Number(targetId);
    setDragId(null);
    if (src === dst) return;
    // Build a new order from the *currently displayed* sortedRoots so
    // the drop position lines up with what the user sees, then persist.
    const currentIds = sortedRoots.map(c => Number(c.id));
    const srcIdx = currentIds.indexOf(src);
    const dstIdx = currentIds.indexOf(dst);
    if (srcIdx < 0 || dstIdx < 0) return;
    const next = [...currentIds];
    next.splice(srcIdx, 1);
    next.splice(dstIdx, 0, src);
    setManualOrder(next);
  };
  const handleDragEnd = () => setDragId(null);

  const firstName = user?.firstName || user?.email?.split('@')[0] || 'there';

  // Manage Customers is an admin-only navigation aid. Owner/Admin can edit
  // customer records; Manager can VIEW the page but goes through the sidebar
  // for that. (Per user request — keep this link tight to administrators.)
  const isAdmin = user?.role === 'Owner' || user?.role === 'Admin';
  const hasCustomers = displayRoots.length > 0;
  const showSkeletons = customersLoading && displayRoots.length === 0;

  const weeklyHighlights = useMemo(
    () => buildWeeklyHighlights(displayRoots, weeklyMetricsByCustomer, weekRange),
    [displayRoots, weeklyMetricsByCustomer, weekRange]
  );

  return (
    <div className="welcome-page">
      <div className="welcome-hero">
        <h1>Welcome, {firstName}!</h1>
        <p>Here's a quick look at how your accounts are doing.</p>
      </div>

      {showLoadProgress && <WelcomeLoadProgress steps={loadSteps} />}

      <section className="welcome-section">
        <div className="welcome-section-head">
          <h2>Your customers</h2>
          {isAdmin && (
            <Link to="/admin/customers" className="welcome-section-link">Manage customers →</Link>
          )}
        </div>

        {/* Comparison-period control + plain-English explanation of what
            drives the metric tile colors. Without this, "why is Logins
            yellow?" was an unanswerable question without diving into the
            code. */}
        <div className="welcome-period-bar">
          <label className="welcome-period-label" htmlFor="welcomeComparisonPreset">
            Compare:
          </label>
          <select
            id="welcomeComparisonPreset"
            className="welcome-period-select"
            value={comparisonPresetId}
            onChange={(e) => setComparisonPresetId(e.target.value)}
            disabled={metricsLoading}
            title="Controls which two windows the metric tile colors compare."
          >
            {COMPARISON_PRESETS.map(p => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>

          <label className="welcome-period-label welcome-sort-label" htmlFor="welcomeSortMode">
            Sort:
          </label>
          <select
            id="welcomeSortMode"
            className="welcome-period-select"
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            title="Controls the order of the customer cards below."
          >
            {SORT_MODES.map(m => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>

          <ViewModeToggle
            className="welcome-view-toggle"
            mode={viewMode}
            onChange={setViewMode}
          />

          <p className="welcome-period-hint">
            Tile colors compare{' '}
            <strong>{comparisonRange.currentLabel}</strong>{' '}
            to <strong>{comparisonRange.previousLabel}</strong>.
            {' '}Green = up &gt;5%, red = down &gt;5%, yellow = within ±5%.
            {sortMode === 'manual' && viewMode === 'cards' && (
              <> {' '}<span className="welcome-sort-hint">Drag a card to reorder.</span></>
            )}
            {viewMode === 'list' && (
              <> {' '}<span className="welcome-sort-hint">Expand a row to see full details.</span></>
            )}
            {sortMode === 'score-desc' && (
              <> {' '}<span className="welcome-sort-hint">Score = 3·referrals + 2·VitalRounds + logins + messages + studies, ±10% trend on referrals.</span></>
            )}
            {(metricsLoading || bgRefreshing) && <span className="welcome-period-loading"> Refreshing…</span>}
          </p>
        </div>

        {showSkeletons ? (
          <div className="welcome-cards">
            {[1, 2, 3].map(n => <WelcomeSkeletonCard key={n} />)}
          </div>
        ) : !hasCustomers ? (
          <div className="welcome-empty">
            <h3>You don't have any customers assigned yet.</h3>
            <p>
              Ask your administrator to assign you one or more customers so you
              can see their health and facilities here.
            </p>
            {isAdmin && (
              <Link className="welcome-cta" to="/admin/customers">Manage customers</Link>
            )}
          </div>
        ) : viewMode === 'list' ? (
          <div className="expandable-list welcome-customer-list">
            {displayRoots.map(c => {
              const subDeals = childrenByParent.get(c.id) || [];
              const childCount = subDeals.length;
              const isMaster = childCount > 0;
              const isExpanded = expandedIds.has(Number(c.id));
              const curr = metricsByCustomer[String(c.id)]?.current || {};
              return (
                <div
                  key={c.id}
                  className={`expandable-list-item ${isExpanded ? 'is-expanded' : ''}`}
                >
                  <div
                    className="expandable-list-row"
                    onClick={() => toggleExpanded(c.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpanded(c.id); } }}
                    role="button"
                    tabIndex={0}
                  >
                    <button
                      type="button"
                      className="expandable-list-expand"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                      onClick={(e) => { e.stopPropagation(); toggleExpanded(c.id); }}
                    >
                      {isExpanded ? '▼' : '▶'}
                    </button>
                    <div className="expandable-list-title">
                      <HealthDot
                        color={c.health?.color}
                        title={c.health?.color ? `Health: ${c.health.color}` : 'Loading health…'}
                      />
                      <span className="expandable-list-title-text">{c.name}</span>
                    </div>
                    {!c._placeholder && (
                      <div
                        className="welcome-list-shortcuts"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <AnalysisShortcutBar
                          compact
                          context={{ kind: 'customer', id: c.id, label: c.name }}
                        />
                      </div>
                    )}
                    <div className="expandable-list-row-meta">
                      {(myAssignments || []).some(g => Number(g.id) === Number(c.id) && g.isPrimary) && (
                        <span className="welcome-badge">Primary</span>
                      )}
                      {isMaster && (
                        <span className="welcome-badge welcome-badge-master">Master · {childCount}</span>
                      )}
                      {!metricsLoading && (
                        <span className="welcome-list-referrals">{curr.referrals ?? 0} referrals</span>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="expandable-list-details">
                      <CustomerCard
                        customer={c}
                        subDeals={subDeals}
                        visibleCustomerIds={myCustomerIds}
                        stats={customerStats}
                        metrics={metricsByCustomer[String(c.id)]}
                        comparisonRange={comparisonRange}
                        metricsLoading={metricsLoading}
                        statsLoading={statsLoading}
                        isPlaceholder={!!c._placeholder}
                        isPrimaryCsRep={(myAssignments || []).some(g => Number(g.id) === Number(c.id) && g.isPrimary)}
                        embedded
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="welcome-cards">
            {displayRoots.map(c => (
              <CustomerCard
                key={c.id}
                customer={c}
                subDeals={childrenByParent.get(c.id) || []}
                visibleCustomerIds={myCustomerIds}
                stats={customerStats}
                metrics={metricsByCustomer[String(c.id)]}
                comparisonRange={comparisonRange}
                metricsLoading={metricsLoading}
                statsLoading={statsLoading}
                isPlaceholder={!!c._placeholder}
                isPrimaryCsRep={(myAssignments || []).some(g => Number(g.id) === Number(c.id) && g.isPrimary)}
                draggable={sortMode === 'manual'}
                isDragging={dragId === Number(c.id)}
                onDragStart={handleDragStart(c.id)}
                onDragOver={handleDragOver}
                onDrop={handleDrop(c.id)}
                onDragEnd={handleDragEnd}
              />
            ))}
          </div>
        )}

        {hasCustomers && !showSkeletons && (
          <WeeklyHighlightsPanel
            highlights={weeklyHighlights}
            weekRange={weekRange}
            loading={weeklyMetricsLoading}
          />
        )}
      </section>
    </div>
  );
}

function WeeklyHighlightsPanel({ highlights, weekRange, loading }) {
  const { summary = [], accounts = [] } = highlights || {};
  const hasContent = summary.length > 0 || accounts.length > 0;

  return (
    <div className="welcome-weekly-panel">
      <div className="welcome-weekly-head">
        <h3 className="welcome-weekly-title">How things are going this week</h3>
        <span className="welcome-weekly-subtitle">
          {weekRange.hasBusinessDays
            ? `${weekRange.currentLabel} vs ${weekRange.previousLabel}`
            : 'Weekends excluded · data through yesterday'}
        </span>
      </div>
      {loading && !hasContent ? (
        <ul className="welcome-weekly-list" aria-busy="true">
          {[1, 2, 3].map(n => (
            <li key={n} className="welcome-weekly-item welcome-weekly-shimmer" />
          ))}
        </ul>
      ) : !hasContent ? (
        <p className="welcome-weekly-empty">No weekly highlights yet — activity data will appear once metrics load.</p>
      ) : (
        <>
          {summary.length > 0 && (
            <ul className="welcome-weekly-list welcome-weekly-summary">
              {summary.map((item, i) => (
                <li
                  key={`s-${i}`}
                  className={`welcome-weekly-item tone-${item.tone}`}
                >
                  <span className="welcome-weekly-dot" aria-hidden="true" />
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          )}
          {accounts.length > 0 && (
            <>
              <h4 className="welcome-weekly-accounts-heading">By account</h4>
              <ul className="welcome-weekly-list welcome-weekly-accounts">
                {accounts.map((item) => (
                  <li
                    key={item.name}
                    className={`welcome-weekly-item welcome-weekly-account tone-${item.tone}`}
                  >
                    <span className="welcome-weekly-dot" aria-hidden="true" />
                    <span>
                      <strong className="welcome-weekly-account-name">{item.name}</strong>
                      {' — '}
                      {item.text}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
      {loading && hasContent && (
        <span className="welcome-weekly-refreshing">Updating…</span>
      )}
    </div>
  );
}

function CustomerCard({
  customer, subDeals, visibleCustomerIds, stats, metrics, comparisonRange, isPrimaryCsRep,
  metricsLoading = false, statsLoading = false, isPlaceholder = false,
  draggable = false, isDragging = false,
  onDragStart, onDragOver, onDrop, onDragEnd,
  embedded = false,
}) {
  const navigate = useNavigate();
  const [showMap, setShowMap] = useState(false);
  const childCount = subDeals.length;
  const isMaster = childCount > 0;
  // Don't chip sub-deals that already have their own card on this dashboard;
  // otherwise the same name shows up twice.
  const chippableSubDeals = subDeals.filter(sd => !visibleCustomerIds?.has(Number(sd.id)));
  const health = customer.health;
  const money = formatMoney(customer.paymentAmount, customer.paymentFrequency);
  const renewal = formatRenewal(customer.contractStartDate, customer.contractTermMonths);

  // For a master, union the child facility counts since that represents the
  // full set of facilities under this deal. Returns BOTH the count and the
  // de-duplicated facility list so the map modal has the full address /
  // lat-long info to plot — the resolved-facilities endpoint (extended in
  // backend Phase 1) now includes street/postalCode/country/lat/lng for
  // every entry.
  const { facilityCount, facilityList } = useMemo(() => {
    const statFor = (id) => stats[id] ?? stats[String(id)];
    if (isMaster) {
      const byId = new Map();
      for (const child of subDeals) {
        const s = statFor(child.id);
        (s?.facilities || []).forEach(f => { if (!byId.has(f.id)) byId.set(f.id, f); });
      }
      const own = statFor(customer.id);
      (own?.facilities || []).forEach(f => { if (!byId.has(f.id)) byId.set(f.id, f); });
      return { facilityCount: byId.size, facilityList: Array.from(byId.values()) };
    }
    const s = statFor(customer.id);
    return {
      facilityCount: s?.facilityCount ?? '—',
      facilityList: s?.facilities || [],
    };
  }, [isMaster, subDeals, stats, customer.id]);

  const curr = metrics?.current || {};
  const prev = metrics?.previous || {};
  const csNames = normalizeCsRepDisplayNames(customer);

  // The whole card is the primary CTA (→ Data Trends), but the shortcut bar
  // at the bottom must navigate to a different page. Use an explicit click
  // handler so the bar's <Link>s can stop propagation rather than living
  // inside a wrapping <Link>, which would also produce invalid nested-anchor
  // markup.
  const goToCustomerData = () => {
    if (isPlaceholder) return;
    navigate(`/customer-data?customerId=${customer.id}`);
  };

  const cardClass = [
    'welcome-card',
    isPlaceholder ? 'welcome-card-placeholder' : '',
    embedded ? 'welcome-card-embedded' : '',
    draggable ? 'is-draggable' : '',
    isDragging ? 'is-dragging' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      role={embedded || isPlaceholder ? 'group' : 'link'}
      tabIndex={embedded || isPlaceholder ? -1 : 0}
      onClick={embedded ? undefined : goToCustomerData}
      onKeyDown={embedded ? undefined : (e) => { if (!isPlaceholder && e.key === 'Enter') goToCustomerData(); }}
      className={cardClass}
      draggable={draggable && !isPlaceholder && !embedded}
      onDragStart={draggable && !embedded ? onDragStart : undefined}
      onDragOver={draggable && !embedded ? onDragOver : undefined}
      onDrop={draggable && !embedded ? onDrop : undefined}
      onDragEnd={draggable && !embedded ? onDragEnd : undefined}
    >
      {draggable && !embedded && (
        <span className="welcome-card-drag-handle" title="Drag to reorder" aria-hidden="true">⋮⋮</span>
      )}
      {!embedded && (
        <div className="welcome-card-header">
          <div className="welcome-card-title">
            <HealthDot color={health?.color} title={health?.color ? `Health: ${health.color}` : 'Loading health…'} />
            <h3>{customer.name}</h3>
          </div>
          <div className="welcome-card-badges">
            {isPrimaryCsRep && <span className="welcome-badge">Primary</span>}
            {isMaster && <span className="welcome-badge welcome-badge-master">Master · {childCount}</span>}
          </div>
        </div>
      )}

      <div
        className={`welcome-card-csrep ${csNames.length === 0 ? 'welcome-card-csrep-empty' : ''}`}
        title={csNames.length > 0 ? csNames.join(', ') : 'No CS rep assigned'}
      >
        <span className="welcome-card-csrep-label">
          {csNames.length <= 1 ? 'Customer success rep' : 'Customer success reps'}
        </span>
        <span className="welcome-card-csrep-names">
          {csNames.length > 0 ? csNames.join(', ') : 'No CS rep assigned'}
        </span>
      </div>

      <div className="welcome-card-stats">
        <div className="welcome-stat">
          <span className="welcome-stat-label">Facilities</span>
          {statsLoading ? (
            <span className="welcome-stat-value welcome-stat-shimmer" aria-label="Loading facilities" />
          ) : Number.isFinite(facilityCount) && facilityCount > 0 ? (
            // Shows the count as a button so the user can pop the
            // map without leaving the page. The card itself is also
            // a clickable region (→ Data Trends), so we stop the
            // event from bubbling up to that handler.
            <button
              type="button"
              className="welcome-stat-value welcome-stat-value-link"
              onClick={(e) => { e.stopPropagation(); setShowMap(true); }}
              title={`Show ${facilityCount} facilit${facilityCount === 1 ? 'y' : 'ies'} on a map`}
            >
              {facilityCount}
            </button>
          ) : (
            <span className="welcome-stat-value">{facilityCount}</span>
          )}
        </div>
        {money && (
          <div className="welcome-stat">
            <span className="welcome-stat-label">Contract</span>
            <span className="welcome-stat-value welcome-stat-money">{money}</span>
          </div>
        )}
      </div>

      <div className="welcome-metric-row">
        <MetricStat label="Referrals"   current={curr.referrals}   previous={prev.referrals}   loading={metricsLoading}
                    currentLabel={comparisonRange?.currentLabel} previousLabel={comparisonRange?.previousLabel} />
        <MetricStat label="VitalRounds" current={curr.vitalRounds} previous={prev.vitalRounds} loading={metricsLoading}
                    currentLabel={comparisonRange?.currentLabel} previousLabel={comparisonRange?.previousLabel} />
        <MetricStat label="Logins"      current={curr.logins}      previous={prev.logins}      loading={metricsLoading}
                    currentLabel={comparisonRange?.currentLabel} previousLabel={comparisonRange?.previousLabel} />
        <MetricStat label="Messages"    current={curr.messages}    previous={prev.messages}    loading={metricsLoading}
                    currentLabel={comparisonRange?.currentLabel} previousLabel={comparisonRange?.previousLabel} />
        <MetricStat label="Studies"     current={curr.studies}     previous={prev.studies}     loading={metricsLoading}
                    currentLabel={comparisonRange?.currentLabel} previousLabel={comparisonRange?.previousLabel} />
      </div>

      {renewal && (
        <div className="welcome-card-renewal">Renews {renewal}</div>
      )}

      {isMaster && chippableSubDeals.length > 0 && (
        <div className="welcome-card-children">
          {chippableSubDeals.slice(0, 4).map(ch => (
            <span key={ch.id} className="welcome-subdeal-chip">
              <HealthDot color={ch.health?.color} size={8} />
              <span className="welcome-subdeal-name">{ch.name}</span>
            </span>
          ))}
          {chippableSubDeals.length > 4 && (
            <span className="welcome-subdeal-chip more">+{chippableSubDeals.length - 4}</span>
          )}
        </div>
      )}

      {!embedded && (
        <AnalysisShortcutBar
          label="Analysis:"
          context={{ kind: 'customer', id: customer.id, label: customer.name }}
        />
      )}

      {showMap && (
        <FacilityMapModal
          title={customer.name}
          subtitle={`${facilityList.length} facilit${facilityList.length === 1 ? 'y' : 'ies'}`}
          facilities={facilityList}
          onClose={() => setShowMap(false)}
        />
      )}
    </div>
  );
}

export default Welcome;
